"use client";

import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { useRouter } from "next/navigation";

export default function TermsOfServicePage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[#faf8f5] dark:bg-gray-950">
      {/* Header with back button */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 pt-16 md:pt-20">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <button
            onClick={() => router.back()}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-purple-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Terms of Service
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Introduction */}
        {/* <div className="mb-12">
          <p className="text-gray-700 leading-relaxed">
            Welcome to LilyLanka where the beauty, culture, and spirit of Sri Lanka
            come together in one seamless digital experience. From sun-kissed beaches and
            misty hilltops to ancient cities and vibrant local life, LilyLanka brings the entire
            island to your fingertips. Whether you're planning your first adventure or
            rediscovering your homeland, we're here to make every moment simple, safe, and
            unforgettable.
          </p>
        </div> */}

        {/* Sustainable Practices Box */}
        {/* <div className="bg-purple-50 border-l-4 border-purple-600 rounded-r-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            These Practices Work for Sri Lanka
          </h2>
          <div className="space-y-4">
            {[
              "Abundant sunshine and long days make solar power especially effective.",
              "Even tropical climate pairs well with natural ventilation and shade-based design.",
              "Remote stays — rural homes, beach huts, and boutique villas — can adopt low-impact systems easily, often at low cost.",
              "Local communities often support organic gardens, use local materials, and value traditional craftsmanship — giving hosts access to cost-effective eco-friendly supplies.",
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                <p className="text-gray-700 text-sm leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div> */}

        {/* Additional text */}
        {/* <div className="mb-12">
          <p className="text-gray-700 leading-relaxed">
            Welcome to LilyLanka where the beauty, culture, and spirit of Sri
            Lanka come together in one seamless digital experience. From
            sun-kissed beaches and misty hilltops to ancient cities and vibrant
            local life, LilyLanka brings the entire island to your fingertips.
            Whether you're planning your first adventure or rediscovering your
            homeland, we're here to make every moment simple, safe, and
            unforgettable.
          </p>
        </div> */}

        {/* Table of Contents */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Table of Contents
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <ul className="space-y-3">
              {[
                { label: "A. All Travel Experiences", id: "section-a" },
                { label: "B. Accommodations", id: "section-b" },
                { label: "C. Attractions", id: "section-c" },
                // { label: "D. Car Rentals", id: "section-d" },
                // { label: "E. Flights", id: "section-e" },
                { label: "F. Private and Public Transport", id: "section-f" },
              ].map((item) => (
                <li key={item.id}>
                  <a
                    href={`#${item.id}`}
                    className="text-purple-600 hover:text-purple-700 hover:underline font-medium"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Section A - General Terms */}
        <section id="section-a" className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 pb-3 border-b-2 border-purple-600">
            Section A – General Terms for All Travel Experiences
          </h2>

          <div className="space-y-8">
            {/* A1 */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                A1. Definitions
              </h3>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                Certain words used in these Terms have specific meanings. These
                definitions form part of this Agreement.
              </p>
            </div>

            {/* A2 */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                A2. About These Terms
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  By completing a booking through Lily Lanka, you agree to these
                  Terms and any additional conditions shown during the booking
                  process.
                </li>
                <li>
                  If any provision is found invalid or unlawful under Sri Lankan
                  law, the remaining provisions remain effective.
                </li>
                <li>
                  Section A applies to all services. Sections B–F contain
                  additional terms specific to each service type. Where there is
                  a conflict, the specific section prevails.
                </li>
                <li>
                  The English version of these Terms shall prevail in the event
                  of any inconsistency.
                </li>
              </ol>
            </div>

            {/* A3 */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                A3. About Lily Lanka
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Lily Lanka (Pvt) Ltd operates and manages the online platform
                  that allows customers to search and book travel-related
                  services.
                </li>
                <li>
                  Lily Lanka provides the Platform only and does not directly
                  provide accommodations, flights, attractions, transport, or
                  rental vehicles unless expressly stated.
                </li>
                <li>
                  Travel services are provided by independent third-party
                  Service Providers.
                </li>
              </ol>
            </div>

            {/* A4 */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                A4. Our Platform
              </h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    1. Accuracy of Information
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    All information, descriptions, content, and materials made
                    available on the Platform are supplied by independent
                    Service Providers. While Lily Lanka exercises reasonable
                    care in presenting such information, Lily Lanka does not
                    represent, warrant, or guarantee the accuracy, completeness,
                    reliability, adequacy, or timeliness of any such information
                    and shall not be liable for any errors, omissions, or
                    inaccuracies therein.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    2. No Endorsement
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    The listing, advertisement, or display of any service,
                    product, or Service Provider on the Platform shall not be
                    construed as an endorsement, recommendation, approval,
                    warranty, or guarantee by Lily Lanka of the quality,
                    suitability, legality, or performance of such service or
                    Service Provider.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    3. No Contractual Relationship
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    Lily Lanka operates solely as an intermediary and technology
                    platform facilitating connections between users and Service
                    Providers. Lily Lanka is not a party to, and shall not be
                    deemed to have entered into, any contract, agreement, or
                    arrangement between users and Service Providers, and shall
                    bear no responsibility or liability arising out of or in
                    connection with any such contract or the performance or
                    non-performance thereof.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    4. Account Registration and Security
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    Access to certain features of the Platform may require
                    registration of an account. Users agree to provide true,
                    accurate, current, and complete information and to promptly
                    update such information as necessary. Users shall be solely
                    responsible for maintaining the confidentiality and security
                    of their account credentials and for all activities
                    conducted under their account. Lily Lanka shall not be
                    liable for any loss or damage arising from unauthorized
                    access to or use of a user's account.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    5. Eligibility
                  </h4>
                  <p className="text-gray-700 leading-relaxed">
                    The Platform is intended solely for individuals who are
                    eighteen (18) years of age or older and who possess the
                    legal capacity to enter into binding contracts. By accessing
                    or using the Platform, users represent and warrant that they
                    meet these eligibility requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* A5 */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
                A5. Our Values
              </h3>
              <p className="text-gray-700 mb-2">You agree to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                <li>Comply with all Sri Lankan laws and regulations</li>
                <li>Avoid fraudulent or false bookings</li>
                <li>Use the Platform responsibly</li>
                <li>Treat Service Providers and staff respectfully</li>
                <li>Avoid causing damage or nuisance</li>
              </ul>
            </div>

            {/* A6-A17 continue similarly... */}
            {/* I'll add a few more key sections */}

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A6. Pricing
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  All prices shown include applicable charges and taxes unless
                  otherwise stated.
                </li>
                <li>
                  Obvious pricing errors or misprints are not binding and may
                  result in cancellation and refund.
                </li>
                <li>
                  Final charges may vary depending on Service Provider policies
                  or currency exchange.
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A7. Payment
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Payments may be processed either: o directly by Lily Lanka on
                  behalf of the Service Provider, or o directly by the Service
                  Provider at check-in or service commencement.
                </li>
                <li>
                  Some bookings require advance or non-refundable payment.
                </li>
                <li>
                  Banks or card providers may charge additional foreign currency
                  or transaction fees.
                </li>
                <li>
                  You must report any unauthorized or fraudulent payments
                  immediately.
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A8. Policies
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Each booking is subject to the Service Provider’s policies
                  including cancellation, refunds, deposits, age limits, and
                  other rules
                </li>
                <li>
                  Cancellation fees depend on the specific provider’s policy.
                </li>
                <li>
                  You are responsible for all persons included in your booking.
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A9. Privacy
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Personal data is processed in accordance with Lily Lanka’s
                  Privacy Policy and applicable Sri Lankan data protection laws.
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A10. Accessibility
              </h3>
              <ol className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Platform assistance – contact Lily Lanka Customer Support
                </li>
                <li>
                  Service-related assistance – contact the Service Provider
                  directly
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A11.Insurance
              </h3>
              <p>
                Insurance products are subject to separate policies and are not
                governed by these Terms.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A12. Intellectual Property
              </h3>
              <p className="text-gray-700 leading-relaxed">
                All content, trademarks, and technology on the Platform belong
                to Lily Lanka or its licensors.
                <br /> Users may not copy, scrape, reproduce, or use automated
                systems without written permission.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A13. What if Something Goes Wrong?
              </h3>
              <ol className="list-decimal list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>
                  Contact Lily Lanka Customer Support first for any complaint or
                  issue.
                </li>
                <li>
                  We will attempt to resolve disputes fairly and promptly.
                </li>
                <li>
                  Lily Lanka is not liable for service failures caused by
                  third-party Service Providers but will assist in
                  communication.
                </li>
              </ol>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A15. Acceptable Behavior
              </h3>
              <p>We may suspend or cancel accounts or bookings for:</p>
              <ol className="list-disc list-inside space-y-2 text-gray-700 leading-relaxed">
                <li>• Fraud or misuse</li>
                <li>• Abuse or misconduct</li>
                <li>Violation of these Terms</li>
              </ol>
              <p>Refund eligibility may depend on circumstances.</p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A16. Governing Law & Jurisdiction
              </h3>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of the Democratic Socialist
                Republic of Sri Lanka. Any dispute shall be subject to the
                exclusive jurisdiction of the courts of Colombo, Sri Lanka.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                A17. Modifications
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Lily Lanka may update these Terms at any time. Continued use of
                the Platform indicates acceptance of the revised Terms.
              </p>
            </div>
          </div>
        </section>

        {/* Section B - Accommodations */}
        <section id="section-b" className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-purple-600">
            Section B – Accommodations (Specific Terms)
          </h2>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B1. Scope
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Applies to hotels, guesthouses, villas, apartments, and other
                lodging services.
              </p>
            </div>

            {/* B2-B6 - Rest of Section B */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B2. Contractual Relationship
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Your contract is directly with the Accommodation provider. Lily
                Lanka only facilitates the booking.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B3. Our Role
              </h3>
              <p className="text-gray-700 mb-2">We:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                <li>Display available properties</li>
                <li>Process bookings</li>
                <li>Send confirmations</li>
                <li>Provide customer support</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B4. Your Responsibilities
              </h3>
              <p className="text-gray-700 mb-2">You must:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                <li>Provide accurate details</li>
                <li>Follow house rules</li>
                <li>Respect property and staff</li>
                <li>Report any damage immediately</li>
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B5. Cancellations & Refunds
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Governed by the property's cancellation policy stated at
                booking.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B6. Damage Policy
              </h3>
              <p className="text-gray-700 leading-relaxed">
                You may be responsible for loss or damage caused during your
                stay. Providers may charge reasonable costs.
              </p>
            </div>

            {/* B7 - Additional Information */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                B7. Additional Information
              </h3>

              {/* Price Match Guarantee */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  1. Lily Lanka Price Match Guarantee
                </h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  We aim to offer you the best available price. If, after making
                  a confirmed booking for an Accommodation through Lily Lanka,
                  you find the same accommodation (same property, room type,
                  dates, conditions, and policies) offered at a lower publicly
                  available price on another website, we will refund the
                  difference.
                </p>
                <p className="text-gray-700 mb-2">
                  To make a claim, please contact our Customer Support by email
                  or telephone and provide:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                  <li>a link to the lower-priced offer, and</li>
                  <li>
                    a clear screenshot showing the price and booking conditions.
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2">
                  The competing offer must still be available online at the time
                  we verify it.
                </p>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 mt-4">
                  <h5 className="font-semibold text-gray-900 mb-2">
                    Price Match Requirements
                  </h5>
                  <p className="text-sm text-gray-700 mb-2">
                    The competing offer must:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                    <li>
                      be for the same property and room/accommodation type
                    </li>
                    <li>have identical check-in and check-out dates</li>
                    <li>have the same cancellation terms and policies</li>
                    <li>be for the same number of occupants</li>
                    <li>include the same meal plan/add-ons</li>
                    <li>be in the same local currency</li>
                  </ul>

                  <p className="text-sm text-gray-700 mt-3 mb-2">
                    Claims are not valid if:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                    <li>
                      the property or room type is hidden until after purchase
                    </li>
                    <li>
                      the website appears unreliable, opaque, or fraudulent
                    </li>
                    <li>
                      the price is part of loyalty, membership, coupon, or
                      rewards programs
                    </li>
                    <li>the booking has been cancelled</li>
                    <li>
                      the property is a single-unit or exclusive property not
                      generally available elsewhere
                    </li>
                  </ul>

                  <p className="text-sm text-gray-700 mt-3 italic">
                    Lily Lanka reserves the sole discretion to determine
                    eligibility for any Price Match claim.
                  </p>
                </div>
              </div>

              {/* Partner Offers */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  2. Partner Offers
                </h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Some listings may be marked as "Partner Offers." These are
                  supplied through authorized third-party partners rather than
                  directly by the Service Provider.
                </p>
                <p className="text-gray-700 mb-2">Unless otherwise stated:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                  <li>payment is required at booking</li>
                  <li>bookings cannot be modified</li>
                  <li>cancellation is allowed only if stated</li>
                  <li>offers cannot be combined with other promotions</li>
                  <li>
                    invoices may be issued by the partner or property (not Lily
                    Lanka)
                  </li>
                </ul>
              </div>

              {/* Promotional Discounts */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  3. Lily Lanka Promotional Discounts
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Some price reductions are funded by Lily Lanka as marketing or
                  promotional incentives. These discounts may not be funded by
                  the Service Provider.
                </p>
              </div>

              {/* Request to Book */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  4. Request to Book
                </h4>
                <p className="text-gray-700 leading-relaxed">
                  Some properties may require approval before confirmation.
                  Where "Request to Book" appears, the Service Provider must
                  confirm availability before the reservation becomes final.
                </p>
              </div>

              {/* Damage Policy Detailed */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-2">
                  5. Damage Policy (Accommodations)
                </h4>
                <p className="text-gray-700 leading-relaxed mb-2">
                  Some Service Providers operate a damage policy. If any guest
                  causes loss or damage:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                  <li>you must inform the property immediately</li>
                  <li>
                    the Service Provider may submit a claim within 14 days after
                    check-out
                  </li>
                  <li>Lily Lanka will notify you and allow you to respond</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2">
                  If you agree to the charge, we may process payment on their
                  behalf. If you dispute the charge, we will review and assist
                  both parties. Service Providers may pursue claims directly
                  under Sri Lankan law outside this process.
                </p>
                <p className="text-gray-700 mt-2 mb-2">
                  This policy does not apply to:
                </p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                  <li>normal wear and tear</li>
                  <li>routine cleaning</li>
                  <li>non-physical penalties (e.g., smoking fines)</li>
                  <li>criminal matters</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-2">
                  Damage deposits taken at check-in are handled solely between
                  you and the Service Provider. Lily Lanka is not responsible
                  for deposit handling.
                </p>
              </div>

              {/* How We Work */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">
                  6. How We Work
                </h4>
                <p>For transparency regarding:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                  <li>reviews</li>
                  <li>rankings</li>
                  <li>commissions</li>
                  <li>fees</li>
                  <li>and how Lily Lanka earns revenue</li>
                </ul>
                <p>
                  please refer to our How We Work page, which forms part of
                  these Terms.
                </p>
              </div>
            </div>

            {/* Section C - Attractions */}
            <section id="section-c" className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-purple-600">
                Section C – Attractions
              </h2>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C1. Scope
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    This section applies to tours, activities, events,
                    experiences, and attraction tickets.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C2. Contractual Relationship
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-2">
                    When you book an Attraction, your contract is directly with:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                    <li>the Service Provider, or</li>
                    <li>an authorized Third-Party Aggregator.</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    Lily Lanka acts only as a booking platform.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C3. Our Role
                  </h3>
                  <p className="text-gray-700 mb-2">We:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                    <li>display listings</li>
                    <li>process bookings</li>
                    <li>send confirmations</li>
                    <li>provide customer assistance</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C4. Your Responsibilities
                  </h3>
                  <p className="text-gray-700 mb-2">You must:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                    <li>provide correct contact details</li>
                    <li>comply with provider terms</li>
                    <li>arrive on time</li>
                    <li>follow safety rules</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mt-2">
                    Failure may result in cancellation or additional charges.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C5. Price & Payment
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Payment is processed according to Section A (Payment).
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C6. Amendments & Refunds
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Subject to each provider's cancellation policy.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    C7. Additional Information
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    See How We Work.
                  </p>
                </div>
              </div>
            </section>

            {/* Section F - Transport */}
            <section id="section-f" className="mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-purple-600">
                Section F – Private & Public Transport
              </h2>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F1. Scope
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Covers taxis, airport transfers, shuttles, buses, trains,
                    and similar transport services.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F2. Contractual Relationship
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Your contract is directly with the Service Provider.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F3. Our Role
                  </h3>
                  <p className="text-gray-700 mb-2">We:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                    <li>facilitate booking</li>
                    <li>pass your details to the provider</li>
                    <li>send confirmation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F4. Your Responsibilities
                  </h3>
                  <p className="text-gray-700 mb-2">You must:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 leading-relaxed ml-4">
                    <li>arrive at pick-up point on time</li>
                    <li>provide correct contact details</li>
                    <li>behave appropriately</li>
                    <li>ensure vehicle suitability</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F5. Price & Payment
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Payment terms are shown at booking. Tolls, taxes, or
                    surcharges may apply.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F6. Cancellations & Refunds
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Policies depend on the provider. Late cancellations may not
                    be refundable.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    F7. Additional Charges
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    You may be liable for cleaning, repair, or misconduct costs.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </section>

        {/* Dictionary */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-3 border-b-2 border-purple-600">
            Dictionary (Definitions)
          </h2>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
            <dl className="space-y-4">
              {[
                {
                  term: "Account",
                  def: "Your registered Lily Lanka user account.",
                },
                {
                  term: "Accommodation",
                  def: "Lodging services provided by a Service Provider.",
                },
                {
                  term: "Attraction",
                  def: "Tours, activities, or experiences.",
                },
                {
                  term: "Booking",
                  def: "A reservation made through the Platform.",
                },
                {
                  term: "Lily Lanka",
                  def: "Lily Lanka (Pvt) Ltd, Colombo, Sri Lanka.",
                },
                { term: "Platform", def: "Lily Lanka website/app." },
                {
                  term: "Service Provider",
                  def: "Third party providing the travel service.",
                },
              ].map((item, index) => (
                <div key={index}>
                  <dt className="font-semibold text-gray-900">"{item.term}"</dt>
                  <dd className="text-gray-700 ml-4 mt-1">{item.def}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Last Updated */}
        <div className="text-center pt-8 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500">Last Updated: February 2026</p>
        </div>
      </div>
    </div>
  );
}
